import { register, Hanko } from '@teamhanko/hanko-elements';

export type HankoEventName =
    | 'sessionCreated'
    | 'sessionExpired'
    | 'userLoggedOut'
    | 'accountDeleted';

export interface HankoClient {
    logout(): Promise<void>;
    on(event: HankoEventName, handler: () => void): () => void;
}

let hankoClientInstance: HankoClient | null = null;
let hankoRegistrationPromise: Promise<HankoClient> | null = null;

export const getHankoApiUrl = (): string => {
    return import.meta.env.VITE_HANKO_API_URL || 'http://localhost:8000';
};

const createHankoClient = (hanko: Hanko): HankoClient => ({
    logout: async () => {
        await hanko.logout();
    },
    on: (event: HankoEventName, handler: () => void) => {
        switch (event) {
            case 'sessionCreated':
                return hanko.onSessionCreated(handler);
            case 'sessionExpired':
                return hanko.onSessionExpired(handler);
            case 'userLoggedOut':
                return hanko.onUserLoggedOut(handler);
            case 'accountDeleted':
                return hanko.onUserDeleted(handler);
            default:
                return () => {};
        }
    },
});

export function registerHankoElements(apiUrl: string): Promise<HankoClient> {
    if (!hankoRegistrationPromise) {
        hankoRegistrationPromise = Promise.resolve()
            .then(() => register(apiUrl))
            .then(({ hanko }) => {
                hankoClientInstance = createHankoClient(hanko);
                return hankoClientInstance;
            })
            .catch((err: unknown) => {
                hankoRegistrationPromise = null;
                hankoClientInstance = null;
                console.error('Failed to register Hanko elements', err);
                throw err;
            });
    }

    return hankoRegistrationPromise;
}

export async function getHankoClient(apiUrl: string): Promise<HankoClient> {
    if (hankoClientInstance) {
        return hankoClientInstance;
    }

    return registerHankoElements(apiUrl);
}

export function _resetHankoState(): void {
    hankoClientInstance = null;
    hankoRegistrationPromise = null;
}
